import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Target,
  Warehouse,
  TrendingUp,
  RefreshCw,
  Zap,
  Loader2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API, PRODUCT_LABELS, fmtChf } from "./utils";
import StockMovementsPanel from "./StockMovementsPanel";

const WarehouseStudio = () => {
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState({});
  const [resetting, setResetting] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [a, i] = await Promise.all([
        axios.get(`${API}/admin/analytics`, { headers }),
        axios.get(`${API}/admin/inventory`, { headers }),
      ]);
      setAnalytics(a.data);
      setInventory(i.data);
    } catch (err) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = async () => {
    const confirmed = window.confirm(
      "ALLE Daten unwiderruflich löschen?\n\n" +
        "• Prelaunch-Anmeldungen\n" +
        "• Kontakt-Nachrichten\n" +
        "• Bestellungen + Zahlungen\n" +
        "• AI Content/Image Drafts\n" +
        "• Lagerbestand (wird auf Initial-Werte zurückgesetzt)\n\n" +
        "Admin-Login bleibt erhalten."
    );
    if (!confirmed) return;
    const second = window.prompt('Zur Bestätigung tippe "RESET" ein:');
    if (second !== "RESET") {
      toast.error("Abgebrochen");
      return;
    }
    setResetting(true);
    try {
      const res = await axios.post(
        `${API}/admin/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data.deleted;
      toast.success(`Reset OK: ${d.orders} Orders, ${d.prelaunch_signups} Signups gelöscht`);
      fetchAll();
    } catch (err) {
      toast.error("Fehler beim Reset");
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveStock = async (slug, size) => {
    const key = `${slug}:${size ?? "-"}`;
    const value = Number(editing[key]);
    if (isNaN(value) || value < 0) {
      toast.error("Ungültiger Wert");
      return;
    }
    try {
      await axios.patch(
        `${API}/admin/inventory/${slug}/${size ?? "-"}`,
        { stock: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Gespeichert");
      setEditing((prev) => {
        const n = { ...prev };
        delete n[key];
        return n;
      });
      fetchAll();
    } catch (err) {
      toast.error("Fehler beim Speichern");
    }
  };

  const profit = analytics?.profit_chf ?? 0;
  const profitPositive = profit >= 0;
  const progressCapped = Math.min(analytics?.breakeven_progress_pct ?? 0, 100);

  const bySlug = inventory.reduce((acc, it) => {
    acc[it.slug] = acc[it.slug] || [];
    acc[it.slug].push(it);
    return acc;
  }, {});

  return (
    <section data-testid="warehouse-section" className="space-y-6">
      {/* Break-even hero */}
      <div className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="breakeven-card">
        <div className="flex items-center gap-3 mb-3">
          <Target className="w-5 h-5 text-[#00A651]" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
            Break-Even-Tracker
          </h2>
          <button onClick={fetchAll} className="ml-auto text-white/40 hover:text-[#00A651]">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <div>
            <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
              Verkauft
            </div>
            <div className="font-display text-4xl text-white">
              {analytics?.gloves_sold ?? 0}
            </div>
            <div className="text-white/40 text-xs">
              Handschuhe · {analytics?.bags_sold ?? 0} Bags
            </div>
          </div>
          <div>
            <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
              Umsatz
            </div>
            <div className="font-display text-4xl text-[#00A651]">
              {fmtChf(analytics?.total_revenue_chf)}
            </div>
            <div className="text-white/40 text-xs">
              {analytics?.orders_count ?? 0} Bestellungen
            </div>
          </div>
          <div>
            <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
              Deckungsbeitrag
            </div>
            <div className="font-display text-4xl text-white">
              {fmtChf(analytics?.contribution_chf)}
            </div>
            <div className="text-white/40 text-xs">nach Stripe & Versand</div>
          </div>
          <div>
            <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
              {profitPositive ? "Gewinn" : "Bis Plus"}
            </div>
            <div
              className={`font-display text-4xl ${
                profitPositive ? "text-[#00A651]" : "text-red-400"
              }`}
            >
              {profitPositive ? "+" : ""}
              {fmtChf(profit)}
            </div>
            <div className="text-white/40 text-xs">
              Fixkosten: {fmtChf(analytics?.fixed_costs_chf)}
            </div>
          </div>
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="font-condensed uppercase tracking-widest text-[10px] text-white/50">
              Break-Even-Fortschritt
            </span>
            <span className="text-[#00A651] font-display text-lg">
              {analytics?.gloves_sold ?? 0} / {analytics?.breakeven_units ?? 0}
              <span className="text-white/40 text-xs ml-2">({progressCapped}%)</span>
            </span>
          </div>
          <div className="h-3 bg-black border border-white/10 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                profitPositive ? "bg-[#00A651]" : "bg-[#00A651]/70"
              }`}
              style={{ width: `${progressCapped}%` }}
              data-testid="breakeven-progress-bar"
            />
          </div>
          <p className="text-white/50 text-xs mt-2 leading-relaxed">
            {profitPositive
              ? `Fixkosten gedeckt. Jeder weitere Verkauf ist Gewinn.`
              : `Noch ${Math.max(
                  0,
                  (analytics?.breakeven_units ?? 0) - (analytics?.gloves_sold ?? 0)
                )} Handschuhe bis zur Gewinnzone.`}
          </p>
        </div>
        {/* Offline & Marketing summary — visible only once data exists */}
        {(analytics?.offline_revenue_chf > 0 ||
          analytics?.offline_gloves_sold > 0 ||
          analytics?.giveaway_gloves > 0 ||
          analytics?.giveaway_bags > 0) && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-white/10"
            data-testid="offline-summary"
          >
            <div>
              <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
                Offline Umsatz
              </div>
              <div className="font-display text-2xl text-[#00A651]">
                {fmtChf(analytics?.offline_revenue_chf)}
              </div>
              <div className="text-white/40 text-xs">nicht via Webshop</div>
            </div>
            <div>
              <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
                Offline Verkauft
              </div>
              <div className="font-display text-2xl text-white">
                {analytics?.offline_gloves_sold ?? 0}
                <span className="text-white/40 text-sm ml-1">Handschuhe</span>
              </div>
              <div className="text-white/40 text-xs">
                {analytics?.offline_bags_sold ?? 0} Bags
              </div>
            </div>
            <div>
              <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
                Marketing Giveaway
              </div>
              <div className="font-display text-2xl text-yellow-400">
                {analytics?.giveaway_gloves ?? 0}
                <span className="text-white/40 text-sm ml-1">Handschuhe</span>
              </div>
              <div className="text-white/40 text-xs">
                {analytics?.giveaway_bags ?? 0} Bags verschenkt
              </div>
            </div>
            <div>
              <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
                Bereits enthalten
              </div>
              <div className="text-white/70 text-xs leading-tight mt-1">
                Offline-Umsatz fließt in Deckungsbeitrag und Break-Even. Giveaways werden
                nur vom Bestand abgezogen.
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Inventory */}
      <div className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="inventory-card">
        <div className="flex items-center gap-3 mb-5">
          <Warehouse className="w-5 h-5 text-[#00A651]" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
            Lagerbestand
          </h2>
          <span className="text-[10px] font-mono text-white/40 ml-auto">
            bearbeitbar · auto-save
          </span>
        </div>
        <div className="space-y-6">
          {Object.entries(bySlug).map(([slug, items]) => {
            const totalInit = items.reduce((s, i) => s + (i.initial_stock || 0), 0);
            const totalCur = items.reduce((s, i) => s + (i.stock || 0), 0);
            const sold = totalInit - totalCur;
            const soldPct = totalInit ? Math.round((sold / totalInit) * 100) : 0;
            return (
              <div key={slug} data-testid={`inventory-${slug}`}>
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="font-display text-2xl text-white">
                    {PRODUCT_LABELS[slug] || slug}
                  </h3>
                  <div className="text-right">
                    <div className="text-[#00A651] font-display text-xl">
                      {totalCur}/{totalInit}
                    </div>
                    <div className="text-white/40 text-[10px] font-condensed uppercase tracking-widest">
                      {soldPct}% verkauft
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {items
                    .sort((a, b) =>
                      String(a.size ?? "").localeCompare(String(b.size ?? ""))
                    )
                    .map((it) => {
                      const key = `${slug}:${it.size ?? "-"}`;
                      const editValue = editing[key];
                      const isEdit = editValue !== undefined;
                      const stock = it.stock ?? 0;
                      const warn = stock > 0 && stock <= 3;
                      const out = stock === 0;
                      return (
                        <div
                          key={key}
                          className={`border p-3 ${
                            out
                              ? "border-red-500/30 bg-red-500/5"
                              : warn
                              ? "border-yellow-500/30 bg-yellow-500/5"
                              : "border-white/10 bg-black"
                          }`}
                          data-testid={`inv-cell-${slug}-${it.size ?? "default"}`}
                        >
                          <div className="font-condensed uppercase tracking-widest text-[10px] text-white/50 mb-1">
                            {it.size ? `Größe ${it.size}` : "Stück"}
                          </div>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min={0}
                              max={999}
                              value={isEdit ? editValue : stock}
                              onChange={(e) =>
                                setEditing((p) => ({ ...p, [key]: e.target.value }))
                              }
                              onBlur={() => isEdit && saveStock(slug, it.size)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveStock(slug, it.size);
                              }}
                              className="w-16 bg-transparent border-b border-white/20 focus:border-[#00A651] outline-none text-white font-display text-xl text-center"
                              data-testid={`inv-input-${slug}-${it.size ?? "default"}`}
                            />
                            <span className="text-white/40 text-xs">/ {it.initial_stock}</span>
                          </div>
                          {out && (
                            <div className="text-red-400 text-[10px] mt-1 font-condensed uppercase tracking-widest">
                              Ausverkauft
                            </div>
                          )}
                          {warn && (
                            <div className="text-yellow-400 text-[10px] mt-1 font-condensed uppercase tracking-widest">
                              Knapp
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Manual stock movements (offline sales + marketing giveaways) */}
      <StockMovementsPanel inventory={inventory} onChanged={fetchAll} />

      {/* Revenue chart */}
      {analytics?.revenue_by_day?.length > 0 && (
        <div className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="revenue-card">
          <div className="flex items-center gap-3 mb-5">
            <TrendingUp className="w-5 h-5 text-[#00A651]" />
            <h2 className="font-condensed uppercase tracking-widest text-sm text-white">
              Umsatz (letzte 30 Tage)
            </h2>
          </div>
          <div className="flex items-end gap-1 h-40">
            {(() => {
              const max = Math.max(
                ...analytics.revenue_by_day.map((d) => d.revenue),
                1
              );
              return analytics.revenue_by_day.map((d) => {
                const h = Math.max((d.revenue / max) * 100, 2);
                return (
                  <div
                    key={d.date}
                    className="flex-1 group relative"
                    title={`${d.date}: CHF ${d.revenue.toFixed(2)}`}
                  >
                    <div
                      className="bg-[#00A651]/70 hover:bg-[#00A651] transition-colors w-full"
                      style={{ height: `${h}%` }}
                    />
                    <div className="absolute left-1/2 -translate-x-1/2 -top-6 bg-black px-2 py-0.5 text-[10px] text-white opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap">
                      CHF {d.revenue.toFixed(0)}
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      )}

      {/* Danger Zone */}
      <div
        className="border border-red-500/30 bg-red-500/5 p-6 mt-8"
        data-testid="danger-zone"
      >
        <div className="flex items-center gap-3 mb-3">
          <Zap className="w-5 h-5 text-red-400" />
          <h2 className="font-condensed uppercase tracking-widest text-sm text-red-400">
            Danger Zone
          </h2>
        </div>
        <p className="text-white/60 text-sm leading-relaxed mb-4">
          Setzt <strong>alle Test- und Echt-Daten</strong> zurück: Prelaunch-Anmeldungen,
          Kontakt-Nachrichten, Bestellungen + Zahlungen, AI-Drafts, Lagerbestand. Admin-Login
          bleibt erhalten. Verwende dies nach erfolgreichen Tests, bevor du den Shop offiziell
          launchst.
        </p>
        <button
          onClick={resetAll}
          disabled={resetting}
          data-testid="admin-reset-btn"
          className="inline-flex items-center gap-2 border border-red-500/40 hover:bg-red-500 hover:text-white text-red-400 disabled:opacity-50 font-condensed uppercase tracking-widest text-xs px-5 py-2.5 transition-colors"
        >
          {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
          {resetting ? "Lösche..." : "Alle Daten zurücksetzen"}
        </button>
      </div>
    </section>
  );
};

export default WarehouseStudio;
